import type { ID, Layer, PageItem, Snapshot } from '@proofroom/schema';
import { walkItems } from '../model/snapshotIndex';

export const CREATIVE_OPTION = /creative\s*option/i;

export const isCreativeOptionLayer = (layer: Layer): boolean => CREATIVE_OPTION.test(layer.name);

export interface OptionGroup {
  key: string;
  label: string;
  spreadIndexes: number[];
  layers: Layer[];
  /** the option visible in the snapshot (first visible, else first) */
  defaultLayerId: ID;
}

/** Visibility overrides applied on top of the snapshot's layer visibility. */
export type LayerOverrides = Record<ID, boolean>;

export function layerVisible(layer: Layer | undefined, overrides: LayerOverrides): boolean {
  if (!layer) return true;
  return overrides[layer.id] ?? layer.visible;
}

/**
 * Whether an item is drawn. Effective visibility is the item's own flag AND its layer's, with the
 * viewer's own layer overrides (the creative option switcher) sitting on top of the layer flag.
 * The exporter reports `visible` as the item's own property only, so an item on a hidden layer
 * still says `true` and appears the moment the viewer switches that layer on.
 */
export function itemVisible(item: PageItem, layers: Record<ID, Layer>, overrides: LayerOverrides): boolean {
  return item.visible !== false && layerVisible(layers[item.layerId], overrides);
}

/**
 * Painting order: strictly back to front on the exporter's `z`, which comes from InDesign's own
 * `allPageItems` and already puts master show-through items below the page's own items and
 * interleaves layers correctly.
 */
export function paintOrder(a: PageItem, b: PageItem): number {
  return a.z - b.z;
}

/** Items to draw, in painting order. */
export function visibleItems(items: PageItem[], layers: Record<ID, Layer>, overrides: LayerOverrides): PageItem[] {
  return items.filter((it) => itemVisible(it, layers, overrides)).sort(paintOrder);
}

function spreadsOfLayer(snapshot: Snapshot, layerId: ID): number[] {
  const out: number[] = [];
  snapshot.spreads.forEach((s, i) => {
    let found = false;
    walkItems(s.items, (it) => { if (it.layerId === layerId) found = true; });
    if (found) out.push(i);
  });
  return out;
}

function rangeLabel(spreads: number[], snapshot: Snapshot): string {
  if (spreads.length === 0) return 'Unused';
  const pages = (i: number) => snapshot.spreads[i]?.pages.map((p) => p.number).join('-') ?? String(i + 1);
  const first = spreads[0], last = spreads[spreads.length - 1];
  return first === last ? `Pages ${pages(first)}` : `Pages ${pages(first)} to ${pages(last)}`;
}

/**
 * Layers whose name contains "creative option", grouped by the set of spreads they appear on.
 * Each group is a radio set: exactly one option is visible at a time.
 */
export function creativeOptionGroups(snapshot: Snapshot): OptionGroup[] {
  const options = snapshot.layers.filter(isCreativeOptionLayer).sort((a, b) => a.index - b.index);
  const byRange = new Map<string, { spreads: number[]; layers: Layer[] }>();
  for (const layer of options) {
    const spreads = spreadsOfLayer(snapshot, layer.id);
    const key = spreads.join(',') || 'none';
    const g = byRange.get(key) ?? { spreads, layers: [] };
    g.layers.push(layer);
    byRange.set(key, g);
  }
  return [...byRange.entries()].map(([key, g]) => ({
    key,
    label: rangeLabel(g.spreads, snapshot),
    spreadIndexes: g.spreads,
    layers: g.layers,
    defaultLayerId: (g.layers.find((l) => l.visible) ?? g.layers[0]).id,
  }));
}

/** Overrides that make exactly `chosen` visible within its group and hide the rest. */
export function chooseOption(group: OptionGroup, chosen: ID, current: LayerOverrides): LayerOverrides {
  const next = { ...current };
  for (const l of group.layers) next[l.id] = l.id === chosen;
  return next;
}
